import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalService } from '../../../core/services/modal.service';
import { InvoiceService } from '../../../core/services/invoice.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { formatUserFacingError } from '../../../core/utils/firebase-error.util';
import { InvoiceDocumentType } from '../../../core/models/invoice.model';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { SelectComponent } from '../../../shared/ui/forms/select/select.component';
import { InputComponent } from '../../../shared/ui/forms/input/input.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';

@Component({
  selector: 'app-upload-invoice-document-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    SelectComponent,
    InputComponent,
    TextComponent,
    LucideIconComponent,
  ],
  templateUrl: './upload-invoice-document-modal.component.html',
})
export class UploadInvoiceDocumentModalComponent {
  private readonly modalService = inject(ModalService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly toastService = inject(ToastService);

  // Injected modal data
  protected readonly modalData = this.modalService.state()?.data || {};
  readonly invoiceId: string = this.modalData.invoiceId;
  readonly invoiceNumber: string = this.modalData.invoiceNumber || '';
  readonly onUploaded?: () => void = this.modalData.onUploaded;

  // Form State
  documentType = signal<InvoiceDocumentType>(
    this.modalData.defaultType || 'factoring_schedule'
  );
  documentTitle = signal<string>('');
  documentNotes = signal<string>('');
  selectedFile = signal<File | null>(null);

  // Status
  isDragging = signal<boolean>(false);
  isUploading = signal<boolean>(false);
  errorMessage = signal<string | null>(null);

  readonly typeOptions: Array<{ value: InvoiceDocumentType; label: string }> = [
    { value: 'factoring_schedule', label: 'Factoring Schedule (Settlement)' },
    { value: 'payment_proof', label: 'Payment Proof / Remittance Advice' },
    { value: 'notice_of_assignment', label: 'Notice of Assignment (NOA)' },
    { value: 'invoice_pdf', label: 'Invoice PDF' },
    { value: 'other', label: 'Other / Supporting Document' },
  ];

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) {
      this.handleFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    if (event.dataTransfer?.files?.[0]) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  private handleFile(file: File): void {
    this.errorMessage.set(null);

    // Max 25MB
    if (file.size > 25 * 1024 * 1024) {
      this.errorMessage.set('File size exceeds the 25MB limit.');
      return;
    }

    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowed = ['pdf', 'png', 'jpg', 'jpeg', 'csv', 'xlsx'];
    if (!ext || !allowed.includes(ext)) {
      this.errorMessage.set('Invalid file type. Allowed: PDF, PNG, JPG, CSV, XLSX.');
      return;
    }

    this.selectedFile.set(file);
    if (!this.documentTitle()) {
      this.documentTitle.set(file.name.replace(/\.[^/.]+$/, ''));
    }
  }

  removeFile(): void {
    this.selectedFile.set(null);
  }

  async upload(): Promise<void> {
    const file = this.selectedFile();
    if (!file) {
      this.errorMessage.set('Please select a file to upload.');
      return;
    }

    if (!this.invoiceId) {
      this.errorMessage.set('No invoice identifier found.');
      return;
    }

    this.isUploading.set(true);
    this.errorMessage.set(null);

    try {
      await this.invoiceService.uploadInvoiceDocument(
        this.invoiceId,
        file,
        this.documentType(),
        this.documentTitle() || file.name,
        this.documentNotes()
      );

      this.toastService.show(
        'success',
        `Document "${file.name}" uploaded successfully.`,
        { duration: 4000 }
      );

      if (this.onUploaded) {
        this.onUploaded();
      }

      this.modalService.close();
    } catch (err: any) {
      console.error('Failed to upload invoice document:', err);
      this.errorMessage.set(formatUserFacingError(err, 'Failed to upload document.'));
    } finally {
      this.isUploading.set(false);
    }
  }

  cancel(): void {
    this.modalService.close();
  }
}
